/**
 * pj-load-more-progress.js — Subsistema de paginación PJ (PanamaJack)
 * ------------------------------------------------------------------
 * QUÉ HACE (iso-funcional con el 6.0.0 custom):
 *   PanamaJack sustituye la paginación nativa de Showcase por una propia, basada en un
 *   botón "Ver más" en la colección/búsqueda. Este módulo reúne las 4 caras de esa misma
 *   feature que en el 6.0.0 vivían dispersas en `assets/theme.js`:
 *     1) Inicialización del contador + barra de progreso al cargar la página
 *        (antiguo bloque `DOMContentLoaded` con guard de IDs).
 *     2) Click en `#load-more`: fetch de la siguiente página con `?view=ajax`, anexar los
 *        `.product-block` al `#product-list`, actualizar contador y barra, e incrementar
 *        `data-next-page` (antiguo handler delegado en `document`).
 *     3) `compruebaBoton()`: recalcula contador/barra contando solo productos reales,
 *        oculta `#load-more` cuando ya se muestran todos y oculta `.contenedor_load`
 *        cuando el total es 0.
 *     4) Recálculo tras filtrar por AJAX: en el 6.0.0 esto vivía DENTRO del callback de
 *        `theme.FilterManager.refreshSelects`. Aquí NO se parchea la función del vendor:
 *        se re-engancha observando el reemplazo del `#product-list` con un MutationObserver.
 *
 * REGLA DE NEGOCIO CLAVE: los banners de merchandising (`.collection-grid-banner`) NO
 *   cuentan como producto → se excluyen siempre del contador y del porcentaje de la barra.
 *
 * MARKUP (PJ, en `snippets/search-filter.liquid`, NO rebasado a 10.2.0 — sigue igual):
 *   #product-list  → contenedor del grid; los items son `.product-block`
 *   #load-more     → botón "Ver más"; lleva `data-next-page` (página a pedir)
 *   #loading-spinner → spinner mostrado durante el fetch
 *   #current-products / #total-products → contador "X de N"
 *   #progress-bar  → barra cuyo `width` refleja el progreso
 *   .collection-grid-banner → banners a excluir del conteo
 *
 * COMPATIBILIDAD 10.2.0:
 *   - El carrusel de fotos al hover NO es un custom element: vive en `ProductBlock`
 *     (`theme.js`), que solo se instancia sobre `.cc-product-block` presentes al cargar.
 *     Tras anexar los bloques nuevos hay que disparar `cc:component:load` (el mismo evento
 *     que usa el tema en filtros e infinite scroll) para que el hover funcione en ellos.
 *   - `enable_infinite_scroll` debe quedar OFF para no duplicar paginación (botón + scroll).
 *   - El carrito (`<cart-form>`) y el variant-picker no intervienen aquí; esta feature es
 *     exclusiva de colección/búsqueda.
 *
 * CABLEADO: en FASE C se carga con `<script defer src="{{ 'pj-load-more-progress.js' | asset_url }}">`
 *   desde `layout/theme.liquid`. Inerte si el markup PJ no está presente (todos los accesos al
 *   DOM tienen null-guards; el bloque 6.0.0 petaba si faltaban los IDs — aquí no).
 *
 * Vanilla JS. Idempotente (no duplica listeners ni observers en re-ejecuciones).
 */
(function () {
  'use strict';

  // Guard de doble carga / doble inicialización.
  if (window.PJ_LOAD_MORE_PROGRESS_INIT) return;
  window.PJ_LOAD_MORE_PROGRESS_INIT = true;

  /** IDs/selectores del markup PJ (search-filter.liquid). */
  var SEL = {
    list: 'product-list',
    loadMore: 'load-more',
    spinner: 'loading-spinner',
    current: 'current-products',
    total: 'total-products',
    progress: 'progress-bar'
  };
  var PRODUCT_BLOCK = '.product-block';
  var BANNER_CLASS = 'collection-grid-banner';

  /** @returns {HTMLElement|null} */
  function byId(id) {
    return document.getElementById(id);
  }

  /**
   * Productos reales (excluye banners de merchandising) dentro del grid.
   * @param {HTMLElement} list
   * @returns {HTMLElement[]}
   */
  function realProducts(list) {
    if (!list) return [];
    var blocks = list.querySelectorAll(PRODUCT_BLOCK);
    return Array.prototype.filter.call(blocks, function (el) {
      return !el.classList.contains(BANNER_CLASS);
    });
  }

  /**
   * Recalcula contador + barra de progreso y oculta el botón si ya está todo a la vista.
   * Equivalente a `compruebaBoton()` + el bloque init del 6.0.0. Null-safe: si falta
   * cualquier pieza del markup, no hace nada (no peta).
   */
  function refreshProgress() {
    var list = byId(SEL.list);
    var currentEl = byId(SEL.current);
    var totalEl = byId(SEL.total);
    var progressBar = byId(SEL.progress);
    if (!list || !currentEl || !totalEl || !progressBar) return;

    var valid = realProducts(list);
    var shown = valid.length;
    var total = parseInt(totalEl.textContent, 10);

    currentEl.textContent = shown;

    var loadMore = byId(SEL.loadMore);
    var container = loadMore ? loadMore.closest('.contenedor_load') : null;

    // Colección o filtro sin productos: no mostrar contador, barra ni "Ver más".
    if (!isNaN(total) && total <= 0) {
      if (container) container.style.display = 'none';
      return;
    }

    if (!isNaN(total) && total > 0) {
      var pct = (shown / total) * 100;
      if (pct > 100) pct = 100;
      progressBar.style.width = pct + '%';

      // Oculta el botón cuando ya se muestran todos los productos.
      if (loadMore && shown >= total) {
        loadMore.style.display = 'none';
      }
    }
  }

  /**
   * Maneja el click en "#load-more": pide la siguiente página vía `?view=ajax`, anexa los
   * `.product-block` nuevos al grid e incrementa `data-next-page`. Reusa `refreshProgress`
   * para el contador/barra al terminar.
   * @param {Event} event
   */
  function onDocumentClick(event) {
    var target = event.target;
    if (!target || target.id !== SEL.loadMore) return;

    var button = target;
    var list = byId(SEL.list);
    var totalEl = byId(SEL.total);
    if (!list || !totalEl) return;

    var nextPage = button.getAttribute('data-next-page');
    if (!nextPage) return;

    var spinner = byId(SEL.spinner);

    var url = new URL(window.location.href);
    url.searchParams.set('page', nextPage);
    url.searchParams.set('view', 'ajax');

    if (spinner) spinner.style.display = 'block';
    button.disabled = true;

    fetch(url.toString())
      .then(function (response) {
        return response.text();
      })
      .then(function (data) {
        // Parsea el fragmento HTML del mismo origen (`?view=ajax` de la colección actual)
        // con DOMParser: no ejecuta scripts ni carga sub-recursos del documento parseado
        // (más seguro que `innerHTML` y iso-funcional para extraer los `.product-block`).
        var parsed = new DOMParser().parseFromString(data, 'text/html');

        // Anexa TODOS los `.product-block` devueltos (incluidos banners, para respetar el
        // layout del grid); el conteo posterior es el que excluye banners.
        var newBlocks = parsed.querySelectorAll('#' + SEL.list + ' ' + PRODUCT_BLOCK);
        Array.prototype.forEach.call(newBlocks, function (el) {
          list.appendChild(document.importNode(el, true));
        });

        // El hover que cicla las fotos solo existe en las instancias de ProductBlock
        // creadas al cargar. Los bloques recién anexados no la tienen hasta este evento.
        initAppendedProductBlocks(list);

        // Avanza a la siguiente página.
        button.setAttribute('data-next-page', String(parseInt(nextPage, 10) + 1));

        // Recalcula contador/barra y posible ocultado del botón.
        refreshProgress();
      })
      .catch(function (error) {
        console.error('PJ load-more: error al cargar productos:', error);
      })
      .finally(function () {
        if (spinner) spinner.style.display = 'none';
        button.disabled = false;
      });
  }

  /**
   * Registra los `.cc-product-block` que aún no tienen instancia (los de "Ver más").
   * `ProductBlock` escucha este evento jQuery y solo inicializa los que no llevan
   * `.cc-initialized`, así que los de la primera página no se duplican.
   * @param {HTMLElement} container
   */
  function initAppendedProductBlocks(container) {
    var jq = (window.theme && window.theme.jQuery) || window.jQuery;
    if (!jq || !container) return;
    jq(document).trigger('cc:component:load', ['product-block', container]);
  }

  /**
   * Re-engancha el recálculo tras el AJAX de filtros. En 6.0.0 esto vivía dentro del
   * callback de `FilterManager.refreshSelects`; aquí NO se parchea el vendor: se observa
   * el reemplazo del `#product-list` (el FilterManager hace `$container.html(...)`, lo que
   * sustituye el nodo o sus hijos) y se vuelve a calcular. Idempotente: un solo observer.
   */
  function watchFilterUpdates() {
    if (typeof MutationObserver === 'undefined') return;

    // El FilterManager reemplaza el contenido del contenedor AJAX (que incluye el grid).
    // Observamos en `document.body` los cambios de subárbol y, si afectan al grid, recalcula.
    // Se debouncea con rAF para no recalcular N veces por una sola sustitución masiva.
    var scheduled = false;
    var observer = new MutationObserver(function (mutations) {
      var touchesGrid = mutations.some(function (m) {
        // ¿El cambio afecta a un `#product-list` o a su contenido?
        var node = m.target;
        if (node && node.id === SEL.list) return true;
        if (node && node.closest && node.closest('#' + SEL.list)) return true;
        // ¿Se insertó/recreó el grid completo (caso `$container.html(...)`)?
        return Array.prototype.some.call(m.addedNodes || [], function (added) {
          if (added.nodeType !== 1) return false;
          return (
            added.id === SEL.list ||
            (added.querySelector && added.querySelector('#' + SEL.list))
          );
        });
      });

      if (!touchesGrid || scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        refreshProgress();
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /** Bootstrap. */
  function init() {
    // 1) Estado inicial (contador + barra). Null-safe: en páginas sin el markup no hace nada.
    //    En colecciones vacías el bloque no se renderiza; un filtro AJAX puede volver a pintarlo.
    refreshProgress();

    // 2) Click delegado en el botón "Ver más" (idempotente: listener único a nivel documento).
    //    Se registra siempre: si la colección empieza en 0 productos, el botón llega después.
    document.addEventListener('click', onDocumentClick);

    // 3) Re-enganche post-filtro vía MutationObserver (sin tocar el vendor).
    if (document.body) watchFilterUpdates();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
